import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { api, ApiError, newIdempotencyKey, type SessionStatus } from '../lib/api'
import { Screen, Button, Notice, Field, Staleness } from '../components/Layout'

const POLL_MS = 5000 // §34.3 — the app must not poll faster than every 5 seconds.

/**
 * Charging status — PRD v1.1 §16.
 *
 * The two rules this screen exists to obey:
 *
 *  - **D11.** 充電中 is shown only when the server says `is_charging`. Before that the
 *    state is 啟動中, because the charger accepting a command is not the car drawing
 *    power. Getting this wrong lets a driver walk away from a car that never started.
 *  - **§37.1.** A dropped connection must never be rendered as charging having stopped.
 *    A failed poll shows a connectivity warning; the session state is left alone.
 */
export default function Charging() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()

  const [status, setStatus] = useState<SessionStatus | null>(null)
  const [offline, setOffline] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stopping, setStopping] = useState(false)
  const timer = useRef<number | null>(null)

  const stopKey = useMemo(() => newIdempotencyKey(), [])

  useEffect(() => {
    let cancelled = false

    async function poll() {
      if (!sessionId) return
      try {
        const next = await api.status(sessionId)
        if (cancelled) return
        setStatus(next)
        setOffline(false)
      } catch (e) {
        if (cancelled) return
        // Do NOT clear `status` and do NOT infer that charging stopped (§37.1).
        // Keep showing the last known state, flagged as possibly stale.
        if (e instanceof ApiError && e.status === 0) setOffline(true)
        else if (e instanceof ApiError && e.status === 404) setError(e.message)
      }
      if (!cancelled) timer.current = window.setTimeout(poll, POLL_MS)
    }

    void poll()
    return () => {
      cancelled = true
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [sessionId])

  async function stop() {
    if (!sessionId) return
    setStopping(true)
    setError(null)
    try {
      const view = await api.stopCharging(sessionId, stopKey)
      if (view.message) setError(view.message)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '停止指令送出失敗，請再試一次。')
    } finally {
      setStopping(false)
    }
  }

  if (error && !status) {
    return (
      <Screen title="充電" back="/">
        <Notice tone="error">{error}</Notice>
      </Screen>
    )
  }

  if (!status) {
    return (
      <Screen title="充電" back="/">
        <Notice tone="muted">載入中…</Notice>
      </Screen>
    )
  }

  const finished = ['COMPLETED', 'FINALIZING', 'PAYMENT', 'BILLING'].includes(
    status.state,
  )
  const failed = ['START_TIMEOUT', 'START_FAILED', 'AUTHORIZATION_FAILED'].includes(
    status.state,
  )
  const cancelled = status.state === 'START_CANCELLED'

  return (
    <Screen title={status.station_name ?? '充電中'} back="/">
      <p className="evse-line">
        {status.evse_id} · 接頭 {status.connector_id}
      </p>

      <div className={`state-badge ${status.is_charging ? 'state-on' : ''}`}>
        {status.is_charging
          ? '充電中'
          : status.state === 'STARTING'
            ? '啟動中'
            : status.state === 'STOPPING'
              ? '停止中'
              : failed
                ? '未能開始'
                : cancelled
                  ? '已取消'
                  : finished
                    ? '已結束'
                    : status.state}
      </div>

      {status.state === 'STARTING' && (
        <Notice tone="muted">請確認充電槍已插好，正在等待充電樁回報開始充電。</Notice>
      )}
      {status.state === 'START_TIMEOUT' && (
        <Notice tone="error">
          充電未能開始。請確認充電槍已插好後再試一次。本次未收取費用。
        </Notice>
      )}
      {offline && (
        <Notice tone="warn">
          連線中斷，畫面可能不是最新狀態。充電不會因此停止。
        </Notice>
      )}
      <Staleness seconds={status.data_age_seconds} />

      <section className="card">
        <Field
          label="開始時間"
          value={
            status.started_at
              ? new Date(status.started_at).toLocaleTimeString('zh-TW', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—'
          }
        />
        <Field label="時間" value={formatDuration(status.duration_seconds)} />
        <Field label="電量" value={status.energy_kwh ? `${status.energy_kwh} kWh` : '—'} />
        <Field
          label="功率"
          value={status.power_w ? `${(status.power_w / 1000).toFixed(1)} kW` : '—'}
        />
        {status.rate && <Field label="費率" value={`NT$${status.rate}/${unit(status.rate_unit)}`} />}
        <Field
          /* §16 — estimates must be visually distinct from final amounts. */
          label="預估費用"
          value={
            status.estimated_fee_twd !== null ? (
              <span className="estimate">NT${status.estimated_fee_twd}</span>
            ) : (
              '—'
            )
          }
        />
      </section>

      {status.is_charging && (
        <Button variant="danger" onClick={stop} busy={stopping}>
          停止充電
        </Button>
      )}
      {status.state === 'STARTING' && (
        <Button variant="secondary" onClick={stop} busy={stopping}>
          取消等待
        </Button>
      )}
      {cancelled && (
        <Notice tone="muted">
          已取消等待。充電樁若仍然啟動，這筆紀錄會自動變回充電中，並照常計費。
        </Notice>
      )}
      {(finished || failed || cancelled) && (
        <Button onClick={() => navigate('/history')}>查看充電紀錄</Button>
      )}
      {error && <Notice tone="warn">{error}</Notice>}
    </Screen>
  )
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}` : `${m} 分鐘`
}

function unit(raw: string | null): string {
  return raw?.split('/')[1] ?? 'kWh'
}
